import { useState } from 'react';
import type { Dayjs } from 'dayjs';
import { App, Button, Card, Form, Input, InputNumber, Modal, Select, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { salesApi } from '@/core/api/salesApi';
import type { CreateOpportunityRequest, Opportunity } from '@/core/types/sales';
import { useAuthStore } from '@/core/auth/authStore';
import { PageHeader } from '@/shared/components/PageHeader';
import { formatDateTime } from '@/core/utils/date';
import { formatNumber } from '@/core/utils/format';
import { JalaliDateTimePicker } from '@/shared/components/JalaliDateTimePicker';

type OpportunityFormValues = Omit<CreateOpportunityRequest, 'expectedCloseAtUtc'> & { expectedCloseAtUtc?: Dayjs };

export const OpportunitiesPage = (): React.ReactElement => {
  const { message } = App.useApp();
  const me = useAuthStore((state) => state.me);
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<OpportunityFormValues>();
  const query = useQuery({ queryKey: ['opportunities'], queryFn: () => salesApi.opportunities(1, 100) });
  const pipelines = useQuery({ queryKey: ['pipelines'], queryFn: salesApi.pipelines });
  const selectedPipelineId = Form.useWatch('pipelineId', form);
  const stages = pipelines.data?.find((item) => item.id === selectedPipelineId)?.stages
    ?? pipelines.data?.find((item) => item.isDefault)?.stages
    ?? [];

  const create = useMutation({
    mutationFn: salesApi.create,
    onSuccess: () => {
      message.success('فرصت فروش ثبت شد.');
      setOpen(false);
      form.resetFields();
      void client.invalidateQueries({ queryKey: ['opportunities'] });
    },
    onError: () => message.error('ثبت فرصت فروش ناموفق بود.'),
  });

  const submit = (values: OpportunityFormValues): void => {
    create.mutate({ ...values, expectedCloseAtUtc: values.expectedCloseAtUtc?.toISOString() ?? null, ownerMembershipId: me?.membership.id || '', currency: values.currency || 'IRR' });
  };

  return <div className="page-stack">
    <PageHeader
      title="فرصت‌های فروش"
      description="پیگیری مبلغ، مرحله و نتیجه فرصت‌ها"
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>فرصت جدید</Button>}
    />
    <Card variant="borderless">
      <Table<Opportunity>
        rowKey="id"
        loading={query.isLoading}
        dataSource={query.data?.items}
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          { title: 'عنوان', dataIndex: 'name', render: (value) => <Typography.Text strong>{value}</Typography.Text> },
          { title: 'مبلغ', render: (_, item) => <span dir="ltr">{formatNumber(item.amount)} {item.currency}</span> },
          { title: 'وضعیت', dataIndex: 'status', render: (value) => <Tag color={value === 'won' ? 'green' : value === 'lost' ? 'red' : 'blue'}>{value === 'won' ? 'برنده' : value === 'lost' ? 'از دست رفته' : 'باز'}</Tag> },
          { title: 'تاریخ ثبت', dataIndex: 'createdAtUtc', render: formatDateTime },
          { title: 'تاریخ پیش‌بینی', dataIndex: 'expectedCloseAtUtc', render: formatDateTime },
        ]}
        locale={{ emptyText: 'فرصت فروشی ثبت نشده است.' }}
      />
    </Card>
    <Modal
      open={open}
      title="ثبت فرصت فروش"
      onCancel={() => setOpen(false)}
      onOk={() => form.submit()}
      confirmLoading={create.isPending}
      forceRender
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ amount: 0, currency: 'IRR', pipelineId: pipelines.data?.find((item) => item.isDefault)?.id }}
        onFinish={submit}
      >
        <Form.Item name="name" label="عنوان" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="amount" label="مبلغ" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
        <Form.Item name="currency" label="ارز"><Select options={[{ value: 'IRR', label: 'ریال' }, { value: 'USD', label: 'دلار' }, { value: 'EUR', label: 'یورو' }]} /></Form.Item>
        <Form.Item name="pipelineId" label="قیف"><Select options={pipelines.data?.map((item) => ({ value: item.id, label: item.name }))} /></Form.Item>
        <Form.Item name="stageId" label="مرحله"><Select allowClear options={stages.map((item) => ({ value: item.id, label: item.name }))} /></Form.Item>
        <Form.Item name="expectedCloseAtUtc" label="تاریخ پیش‌بینی نهایی شدن"><JalaliDateTimePicker /></Form.Item>
      </Form>
    </Modal>
  </div>;
};
